import { Box } from "@mui/material";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import * as React from "react";
import { LxImgSorry } from "../../Media";
import { Parent } from "../../Types";

const DELAY_BETWEEN_ERRORS = 10 * 1000; // 10 sec

interface ClickHandler {
  onClick: () => void;
}

function ReloadButton({ onClick }: ClickHandler) {
  return (
    <Button
      sx={{ marginTop: 4 }}
      variant="contained"
      color="primary"
      onClick={onClick}
    >
      Nochmal versuchen
    </Button>
  );
}

function DashboardButton() {
  return (
    <Button
      sx={{ marginTop: 4 }}
      variant="contained"
      color="primary"
      component="a"
      href="/dashboard"
    >
      Zum Dashboard wechseln
    </Button>
  );
}

export function TryAgain(props: ClickHandler) {
  return (
    <Container>
      <Grid container direction="column" alignItems="center">
        <Typography variant="h2" sx={{ marginTop: 15 }}>
          Ups! Da ist etwas schief gelaufen!
        </Typography>
        <LxImgSorry sx={{ mt: 5 }} />
        <ReloadButton onClick={props.onClick} />
      </Grid>
    </Container>
  );
}

function CantRecover() {
  return (
    <Container>
      <Grid container direction="column" alignItems="center">
        <Typography variant="h2" sx={{ marginTop: 15 }}>
          Das Problem scheint weiterhin zu bestehen!
        </Typography>
        <LxImgSorry sx={{ mt: 5, mb: 2 }} />
        <Typography variant="body1">
          Der Fehler scheint trotz neu laden der Seite weiterhin zu bestehen.
        </Typography>
        <Typography variant="body1">
          Bitte versuchen Sie es zu einem späteren Zeitpunkt noch einmal.
        </Typography>
        <DashboardButton />
        <Box sx={{ mt: 5 }}>
          <Alert severity="info">
            Der Fehler wurde automatisch unserem Support-Team gemeldet.
          </Alert>
        </Box>
      </Grid>
    </Container>
  );
}

interface ErrorBoundaryState {
  errorState: "ok" | "tryAgain" | "cantRecover";
  lastErrorTimestamp: number;
}

/**
 * `LxPageErrorBoundary` provides a top-level error boundary.
 *
 * An error boundaries work like a JavaScript `try {} catch {}` block,
 * but for the components tree.
 * `LxPageErrorBoundary` catches JavaScript errors anywhere in its child
 * component tree during rendering, logs those errors, and display a fallback page.
 * 
 * For more information on error boundaries 
 * see [Introducing Error Boundaries](https://reactjs.org/docs/error-boundaries.html#introducing-error-boundaries)
 *
 * @package Pages
 * @noapi
 * @example
export function AppRoute() {
  return (
    <LxPageErrorBoundary>
      <Router basename={process.env.PUBLIC_URL}>
        <Switch>...</Switch>
      </Router>
    </LxPageErrorBoundary>
  );
}
 */
export class LxPageErrorBoundary extends React.Component<
  Parent,
  ErrorBoundaryState
> {
  constructor(props: any) {
    super(props);

    this.state = {
      errorState: "ok",
      lastErrorTimestamp: 0,
    };
  }

  componentDidCatch(error: any, errorInfo: any) {
    console.error("ErrorBoundary::componentDidCatch:", error, errorInfo);

    const timestamp = Date.now();

    const isReoccurring =
      timestamp - this.state.lastErrorTimestamp < DELAY_BETWEEN_ERRORS;

    if (isReoccurring) {
      return this.setState({
        errorState: "cantRecover",
        lastErrorTimestamp: timestamp,
      });
    }

    this.setState({
      errorState: "tryAgain",
      lastErrorTimestamp: timestamp,
    });
  }

  resetErrorState = () => {
    this.setState({
      errorState: "ok",
      lastErrorTimestamp: Date.now(),
    });
  };

  render() {
    if (this.state.errorState === "tryAgain") {
      return <TryAgain onClick={() => this.resetErrorState()} />;
    }

    if (this.state.errorState === "cantRecover") {
      return <CantRecover />;
    }

    return <>{this.props.children}</>;
  }
}
